import { useEffect } from "react";
import { Tooltip } from "@mui/material";
import { Trans, useTranslation } from "react-i18next";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import S3FileUploadConfirmModal from "../modals/S3FileUploadConfirmModal";
import { useMemo, useState } from "react";
import { FileRejection, useDropzone } from "react-dropzone";
import { useUserContext } from "../../contexts/UserContext";
import { getUser } from "../../clients/UserClient";

/** 最大同時ファイルアップロード数 */
const MAX_UPLOAD_FILES: number = 20;

/** アップロード許容するファイル形式 */
const ALLOWED_EXTENSIONS = [
    ".tif",
    ".tiff",
    ".tfw",
    ".jpg",
    ".jgw",
    ".png",
    ".shp",
    ".dbf",
    ".prj",
    ".shx",
    ".cpg",
    ".sbn",
    ".sbx",
    ".geojson",
    ".json",
    ".kml",
    ".las",
    ".laz",
    ".ovr",
    ".dfproj",
    ".dfmodel",
    ".walker",
    ".csv",
    ".xml",
    ".txt",
    ".pdf",
];

const FileUploader = ({ totalSize, handleUpload }: any) => {
    const { t } = useTranslation();
    const [showUploadModal, setShowUploadModal] = useState<boolean>(false);
    const [acceptedFileList, setAcceptedFileList] = useState<File[]>([]);
    const [rejectionMessage, setRejectionMessage] = useState<string | null>(null);
    const { user, getRecentToken } = useUserContext();
    const [errorMessage, setErrorMessage] = useState<string>("");
    const [userStorageMaxSizeGB, setUserStorageMaxSizeGB] = useState<number>(0);

    /**
     * ドロップゾーン定義
     */
    const { getRootProps, getInputProps, isFocused, isDragAccept, isDragReject } = useDropzone({
        onDrop: (acceptedFiles: File[], fileRejections: FileRejection[]) => {
            setRejectionMessage(null);

            // アップロード可能ファイルのフィルタリング
            const filteredFiles: File[] = [];
            const rejectedFiles: string[] = [];
            acceptedFiles.forEach((file) => {
                const ext = file.name.substring(file.name.lastIndexOf(".")).toLowerCase();
                if (ALLOWED_EXTENSIONS.includes(ext)) {
                    filteredFiles.push(file);
                } else {
                    rejectedFiles.push(file.name);
                }
            });
            if (rejectedFiles.length > 0) {
                setRejectionMessage(t("DataManagement.filesNotAllowed") + rejectedFiles.join(", "));
            }

            // reduce関数で配列の各要素を反復処理し、acc(累積値)にfile.sizeを加算していきファイルサイズ数の合計値を得る
            const totalSizeOfMultipleFilesTryingToUpload = filteredFiles.reduce((acc, file) => acc + file.size, 0);
            const MAX_STORAGE_SIZE: number = userStorageMaxSizeGB * 1024 * 1024 * 1024;
            const newTotalSize = totalSize + totalSizeOfMultipleFilesTryingToUpload;
            if (filteredFiles.length + acceptedFileList.length > MAX_UPLOAD_FILES) {
                setRejectionMessage(t("DataManagement.exceedsMaxFiles"));
                return;
            }
            console.log("FileUploader, userStorageMaxSize:", userStorageMaxSizeGB);
            console.log("FileUploader, userStorageMaxSize Type:", typeof userStorageMaxSizeGB, userStorageMaxSizeGB);
            console.log("FileUploader, newTotalSize, MAX_STORAGE_SIZE:", newTotalSize, MAX_STORAGE_SIZE);
            if (newTotalSize > MAX_STORAGE_SIZE) {
                setRejectionMessage(t("DataManagement.exceedsStorageLimit"));
                return;
            }
            setAcceptedFileList((prevList) => [...prevList, ...filteredFiles]);
        },
        onDropRejected: () => {
            setRejectionMessage(t("DataManagement.cannotUpload"));
        },
    });

    /**
     * ドロップゾーン用スタイル
     */
    const dropZoneStyle = useMemo(() => {
        let combinedStyle =
            "flex flex-col items-center p-10 border-2 border-dashed border-gray-300 rounded bg-gray-100 text-gray-500 outline-none transition-all duration-300";
        if (isFocused) combinedStyle += " border-blue-500";
        if (isDragAccept) combinedStyle += " border-green-500";
        if (isDragReject) combinedStyle += " border-red-500";
        return combinedStyle;
    }, [isFocused, isDragAccept, isDragReject]);

    useEffect(() => {
        if (!user) {
            // ページリロード時はコンテキストも初期化されるため、空の場合は一度リターンする
            // コンテキストが生成されユーザー情報が生成された時点で再実行する
            return;
        }
        fetchDBUser();
    }, [user]);

    /**
     * Getting info of DB user
     * @param file
     * @returns
     */
    async function fetchDBUser() {
        try {
            // トークンチェック&取得
            const idToken = await getRecentToken();
            if (!idToken) {
                setErrorMessage(t("ErrorMessage.failToGetIdpToken"));
                return;
            }
            // [API]ユーザー情報（自身）の取得
            const userResponse = await getUser(idToken, user.attributes.sub);
            if (userResponse) {
                console.log("userResponse", userResponse);
                setUserStorageMaxSizeGB(userResponse.storage_size);
            }
        } catch (error) {
            setErrorMessage(t("ErrorMessage.failToConnectStorage"));
            return;
        }
    }

    /**
     * キャンセル処理
     * @param param0
     * @returns
     */
    const handleCancel = async () => {
        setAcceptedFileList([]);
        setRejectionMessage(null);
    };

    return (
        <>
            {/* ファイルアップロード */}
            <div className="mt-2 flex items-center justify-center">
                <p className="text-center text-lg font-bold text-gray-600">{t("DataManagement.uploadFile")}</p>
                <Tooltip
                    style={{}}
                    title={
                        <ul>
                            <li>{t("DataManagement.uploadHelp1")}</li>
                            <li>{t("DataManagement.uploadHelp2")}</li>
                        </ul>
                    }
                    arrow
                >
                    <HelpOutlineIcon
                        fontSize="small"
                        className="ml-1 cursor-pointer text-gray-500 hover:text-blue-500"
                    />
                </Tooltip>
            </div>
            {rejectionMessage && (
                <>
                    {/* アップロード拒否 */}
                    <p className="my-1 text-center font-bold text-red-500">{rejectionMessage}</p>
                </>
            )}
            {acceptedFileList.length == 0 ? (
                <>
                    {/* アップロード用選択ボックス */}
                    <div {...getRootProps({ className: dropZoneStyle })}>
                        <input {...getInputProps()} />
                        <p>{t("DataManagement.selectFile")}</p>
                    </div>
                </>
            ) : (
                <>
                    {/* アップロード確認 */}
                    <div className="flex flex-col items-center rounded border-2 border-dashed border-gray-300 bg-gray-100 p-5 text-gray-500 outline-none transition-all duration-300">
                        <p>{t("DataManagement.uploadBelow")}</p>
                        <ul>
                            {acceptedFileList.map((file) => (
                                <li key={file.name}>{file.name}</li>
                            ))}
                        </ul>
                        {/* キャンセル/アップロード */}
                        <div className="mt-2">
                            <button
                                type="button"
                                onClick={handleCancel}
                                className="mx-5 font-bold text-gray-500 underline"
                            >
                                {t("CommonLabel.cancel")}
                            </button>
                            <button
                                type="button"
                                onClick={() => handleUpload({ acceptedFileList, setAcceptedFileList })}
                                className="mx-5 font-bold text-gray-500 underline"
                            >
                                {t("CommonLabel.upload")}
                            </button>
                        </div>
                    </div>
                </>
            )}
            <div className="my-1 flex items-center justify-center text-sm text-gray-700">
                <Trans
                    i18nKey="DataManagement.uploadNotes"
                    components={{
                        l: <button className="link-text" onClick={() => setShowUploadModal(true)} />,
                    }}
                />
            </div>
            {/* ファイルアップロード確認モーダル */}
            <S3FileUploadConfirmModal showModal={showUploadModal} setShowModale={setShowUploadModal} />
        </>
    );
};
export default FileUploader;
